import React, { useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import map from 'lodash/map';

import {
    Button,
    ButtonGroup,
    Flex,
    FormField,
    Label,
    LabeledControl,
    Radio,
    Spacer,
    Text,
    TextButton,
} from '@zillow/constellation';

import ModalActions from 'app/shared/actions/ModalActions';
import PostingActions from 'app/shared/actions/PostingActions';

import ErrorMessageConstants from 'app/shared/constants/ErrorMessageConstants';
import ModalConstants from 'app/shared/constants/ModalConstants';

import ModalFooter from 'app/shared/components/Modal/ModalFooter';

import {
    StyledMDActionsContainer,
    StyledMDContainer,
    StyledMDContent,
    StyledMDContentContainer,
} from 'app/shared/containers/ModalContainer/StyledComponents';

import localReducer, { ACTION, INIT_STATE } from './reducer';

const RADIO_OPTION = {
    NOT_LOOKING: {
        displayLabel: 'I\'m not looking for a renter',
        modalHeader: 'Thanks for choosing Zillow!',
        modalType: ModalConstants.FEED_LISTING_DEACTIVATE_CONFIRMATION_THANKS_MODAL,
        reason: 'noLongerLooking',
    },
    DO_NOT_WANT_TO_PAY: {
        displayLabel: 'I don\'t want to pay to list this property',
        modalHeader: 'Your listing is deactivated',
        modalType: ModalConstants.FEED_LISTING_DEACTIVATE_CONFIRMATION_MODAL,
        reason: 'doNotWantToPay',
    },
    HAS_ERROR: {
        displayLabel: 'There\'s an error with the listing that I need to resolve',
        modalHeader: 'How to fix listing errors',
        modalType: ModalConstants.FEED_LISTING_DEACTIVATE_CONFIRMATION_HAS_ERROR_MODAL,
        reason: 'errorWithListing',
    },
    OTHER: {
        displayLabel: 'Other',
        modalHeader: 'Your listing is deactivated',
        modalType: ModalConstants.FEED_LISTING_DEACTIVATE_CONFIRMATION_MODAL,
        reason: 'other',
    },
};

const propTypes = {};

const defaultProps = {};

const FeedListingDeactivateModal = ({ updateFormData }) => {
    const dispatch = useDispatch();

    const aliasEncoded = useSelector(state => state.properties.currentProperty.aliasEncoded);

    const [{
        errorMessage,
        hasError,
        isFetching,
        selectedReasonKey,
    }, reducerDispatch] = useReducer(localReducer, INIT_STATE);

    const isCTADisabled = selectedReasonKey === null || isFetching;

    const hideModal = () => {
        dispatch(ModalActions.hideModal());
    };

    const renderModalFooter = (modalType) => {
        switch (modalType) {
            case ModalConstants.FEED_LISTING_DEACTIVATE_CONFIRMATION_HAS_ERROR_MODAL:
                return (
                    <ModalFooter
                        onSubmit={() => {
                            window.location.href = 'mailto:rentalshelp@zillowgroup.com';
                        }}
                        submitText="Email"
                    />
                );
            default:
                return (
                    <ModalFooter
                        onSubmit={hideModal}
                        submitText="OK"
                    />
                );
        }
    };

    const displayModalByKey = (option) => {
        const { modalType, modalHeader } = option;
        const renderedFooter = renderModalFooter(modalType);

        dispatch(
            ModalActions.showModal({
                modalType,
                isFluid: false,
                header: modalHeader,
                footer: renderedFooter ? () => renderedFooter : null,
            })
        );
    };

    const handleChange = (event) => {
        reducerDispatch({
            type: ACTION.UPDATE_REASON_KEY,
            payload: {
                selectedReasonKey: event.target.id,
            },
        });
    };

    const handleDeactivateSuccess = () => {
        // function is passed down multiple children from EditListingPage
        updateFormData({
            active: false,
            syndication: {
                overview: {
                    status: 'inactive',
                    displayStatus: 'inactive',
                    publishedCount: 0,
                },
            },
        });

        displayModalByKey(RADIO_OPTION[selectedReasonKey]);
    };

    const handleDeactivateError = (errorRMWModel) => {
        reducerDispatch({
            type: ACTION.DISPLAY_ERROR,
            payload: {
                errorMessage: errorRMWModel.message,
            },
        });
    };

    const handleDeactivateClick = async () => {
        displayModalByKey(RADIO_OPTION[selectedReasonKey]);
        reducerDispatch({ type: ACTION.FETCH_START });

        const { reason } = RADIO_OPTION[selectedReasonKey];

        dispatch(PostingActions.property.deactivate(aliasEncoded, reason))
            .then(handleDeactivateSuccess)
            .catch(handleDeactivateError);
    };

    const renderLabels = () => map(RADIO_OPTION, (obj, key) => (
        <LabeledControl
            key={key}
            controlId={key}
            labelPosition="right"
            label={(
                <Label>{obj.displayLabel}</Label>
            )}
            control={(
                <Radio
                    name={key}
                    checked={selectedReasonKey === key}
                    onChange={handleChange}
                />
            )}
        />
    ));

    return (
        <StyledMDContainer className="FeedListingDeactivateModal">
            <StyledMDContentContainer>
                <StyledMDContent>
                    <Spacer marginBottom="sm">
                        <Text fontType="h6">
                            The listing will no longer be advertised.
                            Please choose your reason for deactivating it.
                        </Text>
                    </Spacer>

                    <FormField>
                        {renderLabels()}
                    </FormField>
                </StyledMDContent>
            </StyledMDContentContainer>

            <StyledMDActionsContainer>
                <Flex
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-end"
                >
                    {hasError && (
                        <div className="ErrorContainer">
                            <Spacer paddingBottom="xs">
                                <Text fontColor="red600">
                                    {errorMessage || ErrorMessageConstants.DEFAULT_ERROR}
                                </Text>
                            </Spacer>
                        </div>
                    )}

                    <ButtonGroup aria-label="cta-group">
                        <Flex
                            display="flex"
                            flexDirection="row"
                            alignItems="center"
                            justifyContent="flex-end"
                        >
                            <Spacer marginRight="md">
                                <TextButton
                                    data-test="cancel-button"
                                    onClick={hideModal}
                                >
                                    Cancel
                                </TextButton>
                            </Spacer>

                            <Spacer>
                                <Button
                                    data-test="deactivate-listing-button"
                                    buttonType="primary"
                                    onClick={handleDeactivateClick}
                                    disabled={isCTADisabled}
                                >
                                    Yes, deactivate
                                </Button>
                            </Spacer>
                        </Flex>
                    </ButtonGroup>
                </Flex>
            </StyledMDActionsContainer>
        </StyledMDContainer>
    );
};

FeedListingDeactivateModal.propTypes = propTypes;
FeedListingDeactivateModal.defaultProps = defaultProps;

export default FeedListingDeactivateModal;
